import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ProductSummary.module.scss';
import { Image as FiImage } from '../../../../../components/Image';
import ValuePropsBox from '../ValuePropsBox';
import { ReactComponent as IconKit } from '../../../../../assets/images/icons/icon_kit.svg';
import { ReactComponent as IconBase } from '../../../../../assets/images/icons/icon_base.svg';
import { ReactComponent as IconBatteryCharging } from '../../../../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconLte } from '../../../../../assets/images/icons/icon_lte.svg';
import { ReactComponent as IconDrip } from '../../../../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconReinforced } from '../../../../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconFeather } from '../../../../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconBroadcast } from '../../../../../assets/images/icons/icon_broadcast.svg';
import { ReactComponent as IconStats } from '../../../../../assets/images/icons/icon_stats.svg';
import { ReactComponent as IconVets } from '../../../../../assets/images/icons/icon_vets.svg';
import { ReactComponent as IconRank } from '../../../../../assets/images/icons/icon_rank.svg';
import { ReactComponent as IconUpdate } from '../../../../../assets/images/icons/icon_update.svg';
import { showNanoVariant } from '../../../../../components/NanoBanner/NanoBanner';

export default function ProductSummary({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) {
  const { t } = useTranslation();
  const hideMobileAppSection = showNanoVariant();
  return (
    <div ref={containerRef} className={styles.container}>
      <h1 className={styles.heading}>{t('product_details')}</h1>
      <div className={styles.hardwareImage}>
        <FiImage
          image={{
            type: 'image',
            url: '/product_images/smart-collar-v3/unboxing.png',
          }}
        />
      </div>
      <h2 className={styles.subHeading}>{t('collar_specifications')}</h2>
      <div className={styles.valueProps}>
        <ValuePropsBox
          valueProps={[
            {
              icon: <IconKit />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('all_new_fi_series_3_device_collar_included_for_free')}
                    </span>
                  </span>
                </div>
              ),
            },
            {
              icon: <IconBase />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>{t('charging_base_with_usb_cable')}</span>
                  </div>
                </div>
              ),
            },
            {
              icon: <IconBatteryCharging />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('long_battery_life_with_up_to_3_months_from_one_charge')}
                    </span>
                  </span>
                </div>
              ),
            },
            {
              icon: <IconLte />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('unlimited_gps_lte_m_coverage_track_in_most_areas_and_on_hikes')}
                    </span>
                  </span>
                </div>
              ),
            },
            {
              icon: <IconDrip />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('let_them_swim_with_ip68_and_ip66k_water_resistance')}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              icon: <IconReinforced />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('500lbs_plus_resistance_handles_the_worst_chewers_and_pullers')}
                    </span>
                  </span>
                </div>
              ),
            },
            {
              icon: <IconFeather />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    <span className={styles.valuePropHeaderBold}>
                      {t('light_low_profile_and_comfortably_designed_for_small_to_large_dogs')}
                    </span>
                  </span>
                </div>
              ),
            },
          ]}
        />
      </div>
      {!hideMobileAppSection && (
        <>
          <div className={styles.mobileAppImage}>
            <FiImage
              image={{
                type: 'image',
                url: '/product_images/smart-collar-v3/app-ui-homescreen.jpg',
                descriptors: ['1000w'],
              }}
            />
          </div>
          <h2 className={styles.subHeading}>{t('fi_mobile_app')}</h2>
          <div className={styles.valueProps}>
            <ValuePropsBox
              valueProps={[
                {
                  icon: <IconBroadcast />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('24_7_access_to_your_dog_with_instant_escape_alerts_and_live_gps_tracking')}
                        </span>
                      </span>
                    </div>
                  ),
                },
                {
                  icon: <IconStats />,
                  text: (
                    <div className={styles.valueProp}>
                      <div className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('keep_them_active_and_healthy_with_daily_step_goals_and_sleep_tracking')}
                        </span>
                      </div>
                    </div>
                  ),
                },
                {
                  icon: <IconVets />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('connect_your_veterinarian_information_to_share_your_dogs_health_over_time')}
                        </span>
                      </span>
                    </div>
                  ),
                },
                {
                  icon: <IconRank />,
                  text: (
                    <div className={styles.valueProp}>
                      <div className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('share_your_walks_celebrate_milestones_with_the_fi_community')}
                        </span>
                      </div>
                    </div>
                  ),
                },
                {
                  icon: <IconUpdate />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('unlimited_software_updates_on_ios_and_android')}
                        </span>
                      </span>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
